import { features } from '@cocoonspace/shared/config/features'
import { useInView } from 'framer-motion'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useMemo, useRef } from 'react'
import { MainContainer } from '~/components/atoms/main-container'
import {
	SidebarLayoutContent,
	SidebarLayoutSidebar,
	SidebarLayoutWrapper,
} from '~/components/layouts/sidebar-layout'
import { NewsList } from '~/components/molecules/news-list'
import { DeskTypeBadge } from '~/domains/spaces/components/desk-type-badge'
import { SpaceSeoTags } from '~/domains/spaces/components/space-seo-tags'
import { useSpaceDetailsPage } from '~/domains/spaces/hooks/use-space-details-page.hook'
import { useExperiment } from '~/hooks/use-experiment'
import { cn } from '~/lib/utils'
import styles from '../pages/index.module.css'
import { DeskSidebar } from './desk-sidebar/desk-sidebar'
import { SpaceDetailsBreadcrumb } from './space-details-breadcrumb'
import { SpaceDetailsDescription } from './space-details-description'
import { SpaceDetailsNav } from './space-details-nav'
import { SpaceDetailsPhotos } from './space-details-photos'
import { SpaceDetailsSidebar } from './space-details-sidebar'
import { SpaceExtras } from './space-extras'
import { SpaceFeaturesList } from './space-features-list'
import { SpaceLocalisation } from './space-localisation'
import { SpacePrices } from './space-prices'
import { SpaceRules } from './space-rules'
import { type DistrictConfig, SpaceSuggestions } from './space-suggestions'

const SpaceVisitDialog = dynamic(
	() =>
		import('./space-visit/space-visit-dialog').then(
			(mod) => mod.SpaceVisitDialog,
		),
	{ ssr: false },
)

const SpaceDetailsTour = dynamic(
	() => import('./space-details-tour').then((mod) => mod.SpaceDetailsTour),
	{ ssr: false },
)

const SpaceMap = dynamic(() => import('./space-map'), {
	ssr: false,
	loading: () => (
		<div className='h-[500px] w-full animate-pulse bg-slate-200' />
	),
})

export const SpaceDetailsPage = ({
	district,
}: {
	district?: DistrictConfig
}) => {
	const router = useRouter()
	const isOnboardingActive = useExperiment('onboarding')
	const imagesPreview = useRef(null)
	const isInView = useInView(imagesPreview, { margin: '-120px 0px 0px 0px' })

	const spaceId = router.query.slug as string
	const isDesk = router.route === '/desks/[zone]/[slug]'

	const { spaceDetailsQuery, suggestionsQuery } = useSpaceDetailsPage(spaceId)

	const space = spaceDetailsQuery.data
	const isPublished = space?.status === 'published'

	const hasFeatures = useMemo(
		() =>
			!!features.filter((feature) => space?.features?.includes(feature as any))
				.length,
		[space?.features],
	)

	const hasExtras = useMemo(
		() =>
			!!space?.extras?.filter(({ category }) => category !== 'other').length,
		[space?.extras],
	)

	if (!space) return null

	return (
		<div className='flex flex-col pb-8'>
			{!isPublished && (
				<meta
					name='robots'
					content='noindex, nofollow'
				/>
			)}

			<SpaceSeoTags
				district={district}
				space={space}
			/>

			{!isInView && <SpaceDetailsNav spaceTitle={space?.title || ''} />}

			<div
				id='photos'
				className='container mx-auto py-4'
			>
				<SpaceDetailsBreadcrumb
					space={space}
					isDesk={isDesk}
				/>
			</div>

			<div className='flex flex-col gap-6'>
				<MainContainer className='relative gap-0 py-0 lg:pb-4'>
					{isDesk && (
						<DeskTypeBadge
							type={space.desk_type}
							className='absolute top-8 left-0 z-10 ml-auto rounded-l-none p-4 font-base text-sm md:top-6 lg:left-6'
						/>
					)}

					<SpaceDetailsPhotos
						ref={imagesPreview}
						space={space}
					/>

					<SidebarLayoutWrapper>
						<SidebarLayoutContent className='flex flex-col overflow-x-hidden'>
							<NewsList news={space.news} />

							<div className={cn('flex flex-col gap-8', styles.navlink)}>
								<SpaceDetailsDescription
									space={space}
									isDesk={isDesk}
								/>

								{hasFeatures && <SpaceFeaturesList space={space} />}
								{!isDesk && hasExtras && <SpaceExtras space={space} />}
								{!isDesk && <SpacePrices space={space} />}
								<SpaceLocalisation space={space} />
							</div>
						</SidebarLayoutContent>

						<SidebarLayoutSidebar>
							{isDesk ? (
								<DeskSidebar space={space} />
							) : (
								<SpaceDetailsSidebar
									space={space}
									showInfoCard={isInView}
								/>
							)}
						</SidebarLayoutSidebar>
					</SidebarLayoutWrapper>
				</MainContainer>

				<SpaceMap space={space} />

				{!!suggestionsQuery.data?.length && (
					<SpaceSuggestions
						id='suggestions'
						space={space}
						suggestions={suggestionsQuery.data}
						district={district}
					/>
				)}

				{!isDesk && <SpaceRules />}
			</div>

			{isOnboardingActive && <SpaceDetailsTour />}

			<SpaceVisitDialog space={space} />
		</div>
	)
}
